import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, ImagePlus, Loader2 } from "lucide-react";

const time = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

export default function Chat({ elderId, meRole }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const token = localStorage.getItem("candle_token");

  const load = async () => { try { const { data } = await api.get(`/eldercare/messages/${elderId}`); setMessages(data.messages); } catch { /* ignore */ } };
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [elderId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (file) => {
    if (!file && !text.trim()) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", text);
    if (file) fd.append("file", file);
    try { await api.post(`/eldercare/messages/${elderId}`, fd); setText(""); if (fileRef.current) fileRef.current.value = ""; load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 flex flex-col h-[520px]">
      <div className="p-4 border-b border-slate-100 font-head font-semibold text-[#041E26]">Family &amp; Caregiver chat</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && <p className="text-center text-slate-400 text-sm mt-8">No messages yet. Say hello 👋</p>}
        {messages.map((m) => {
          const mine = m.sender_role === meRole;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#041E26] text-white" : "bg-slate-100 text-[#041E26]"}`}>
                {!mine && <div className="text-[11px] font-semibold text-[#D97706] mb-0.5">{m.sender_name} · {m.sender_role === "sathi" ? "Sathi" : "Family"}</div>}
                {m.image_path && <img src={`${API}/eldercare/messages/${elderId}/image/${m.id}?auth=${token}`} alt="attachment" className="rounded-lg mb-1 max-h-52 object-cover" />}
                {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-slate-400"}`}>{time(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-slate-100 flex items-center gap-2">
        <input ref={fileRef} data-testid="chat-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => send(e.target.files?.[0])} />
        <button data-testid="chat-attach-btn" onClick={() => fileRef.current?.click()} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200"><ImagePlus className="w-5 h-5" /></button>
        <Input data-testid="chat-text-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 h-11 rounded-full" />
        <button data-testid="chat-send-btn" onClick={() => send()} disabled={sending} className="w-11 h-11 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] flex items-center justify-center">{sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
      </div>
    </div>
  );
}
